import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';

function parseRpcResult(data) {
  if (data == null) return null;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function StaffNew() {
  const navigate = useNavigate();
  const { id: editStaffId } = useParams();
  const isEdit = Boolean(editStaffId);

  const [fullName, setFullName] = React.useState('');
  const [role, setRole] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [notes, setNotes] = React.useState('');

  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [loadInitial, setLoadInitial] = React.useState(() => Boolean(editStaffId));
  const [loadError, setLoadError] = React.useState('');

  React.useEffect(() => {
    if (!isEdit || !editStaffId) {
      setLoadInitial(false);
      setLoadError('');
      return undefined;
    }

    if (!supabase) {
      setLoadInitial(false);
      setLoadError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoadInitial(true);
      setLoadError('');
      const { data: row, error } = await supabase
        .from('maintenance_staff')
        .select('*')
        .eq('id', editStaffId)
        .single();

      if (cancelled) return;

      if (error) {
        setLoadError(error.message);
        setLoadInitial(false);
        return;
      }

      setFullName(row.full_name || '');
      setRole(row.role || '');
      setEmail(row.email || '');
      setPhone(row.phone || '');
      setNotes(row.notes || '');
      setPassword('');
      setLoadInitial(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEdit, editStaffId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    const trimmedName = fullName.trim();
    if (!trimmedName) {
      setSaveError('Please enter a full name.');
      return;
    }

    const trimmedEmail = email.trim();
    if (!isEdit && !trimmedEmail) {
      setSaveError('Enter an email — staff use it with their password when signing in at /admin.');
      return;
    }

    if (!isEdit && password.trim().length < 6) {
      setSaveError('Password must be at least 6 characters.');
      return;
    }

    if (isEdit && password.trim() && password.trim().length < 6) {
      setSaveError('New password must be at least 6 characters, or leave blank to keep the current one.');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        const { data, error: rpcError } = await supabase.rpc('update_maintenance_staff', {
          p_id: editStaffId,
          p_full_name: trimmedName,
          p_role: role.trim(),
          p_email: trimmedEmail,
          p_phone: phone.trim(),
          p_notes: notes.trim(),
          p_password: password.trim(),
        });
        if (rpcError) throw rpcError;
        const row = parseRpcResult(data);
        if (!row?.ok) {
          setSaveError(row?.error || 'Could not update staff.');
          return;
        }
      } else {
        const { data, error: rpcError } = await supabase.rpc('create_maintenance_staff', {
          p_full_name: trimmedName,
          p_role: role.trim(),
          p_email: trimmedEmail,
          p_phone: phone.trim(),
          p_notes: notes.trim(),
          p_password: password,
        });
        if (rpcError) throw rpcError;
        const row = parseRpcResult(data);
        if (!row?.ok) {
          setSaveError(row?.error || 'Could not create staff.');
          return;
        }
      }

      setPassword('');
      navigate('/staff');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSaveError(
        err?.message ||
          err?.error_description ||
          'Could not save staff. Run sql/add_staff_supabase.sql then sql/add_maintenance_staff_password_supabase.sql in Supabase.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="staff-root">
      <UniversalHeader />

      <div className="staff-shell">
        <header className="staff-header">
          <div>
            <h1 className="staff-title">
              {isEdit ? 'Edit staff' : 'Add new staff'}
            </h1>
            <p className="staff-subtitle">
              {isEdit
                ? 'Update details or set a new password. They sign in at /admin with role Maintenance Staff using this email and password.'
                : 'Set a password (min 6 characters) so they can sign in at /admin as Maintenance Staff with this email.'}
            </p>
          </div>

          <div className="staff-actions">
            <button
              type="button"
              className="btn-ghost staff-btn"
              disabled={saving || loadInitial}
              onClick={() => navigate('/staff')}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="staff-new-form"
              className="btn-primary staff-btn"
              disabled={saving || loadInitial}
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save staff'}
            </button>
          </div>
        </header>

        {saveError ? (
          <p className="properties-save-error" role="alert">
            {saveError}
          </p>
        ) : null}
        {loadError ? (
          <p className="properties-save-error" role="alert">
            {loadError}{' '}
            <button
              type="button"
              className="btn-ghost staff-btn"
              style={{ marginTop: 8 }}
              onClick={() => navigate('/staff')}
            >
              Back to staff
            </button>
          </p>
        ) : null}

        <main className="staff-layout">
          <section className="staff-list">
            <div className="properties-detail-card">
              {isEdit && loadInitial ? (
                <p className="properties-list-empty" style={{ margin: '20px 0' }}>
                  Loading staff…
                </p>
              ) : null}
              <form
                id="staff-new-form"
                className="properties-add-form"
                onSubmit={handleSubmit}
                style={{
                  display: isEdit && (loadInitial || loadError) ? 'none' : undefined,
                }}
              >
                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="staff-name">
                      Full name
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-name"
                        className="input-element"
                        type="text"
                        placeholder="e.g. Alex Johnson"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="staff-role">
                      Role
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-role"
                        className="input-element"
                        type="text"
                        placeholder="e.g. Plumber"
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="staff-email">
                      Email
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-email"
                        className="input-element"
                        type="email"
                        placeholder="staff@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="staff-phone">
                      Phone
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-phone"
                        className="input-element"
                        type="tel"
                        placeholder="+1 555 000 0000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper" style={{ gridColumn: '1 / -1' }}>
                    <label className="field-label" htmlFor="staff-password">
                      {isEdit ? 'New password (optional)' : 'Password (required)'}
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-password"
                        className="input-element"
                        type="password"
                        name="password"
                        autoComplete={isEdit ? 'new-password' : 'new-password'}
                        placeholder={
                          isEdit
                            ? 'Leave blank to keep current password'
                            : 'Min 6 characters — used at /admin as Maintenance Staff'
                        }
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required={!isEdit}
                      />
                    </div>
                  </div>
                </div>

                <div className="properties-room-row">
                  <div className="field-wrapper">
                    <label className="field-label" htmlFor="staff-notes">
                      Notes
                    </label>
                    <div className="input-shell">
                      <input
                        id="staff-notes"
                        className="input-element"
                        type="text"
                        placeholder="Specialties, availability, etc."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

export default StaffNew;
